/**
 * Coveo Commerce Engine — lazy singleton.
 *
 * Handles: product search, product listing, cart, sort, facets, pagination, URL sync.
 * Backed by: @coveo/headless/commerce
 *
 * SSR-safe: no window references at module scope.
 * Testable: engine creation deferred until initCommerceEngine() is called.
 */
import {
  buildCommerceEngine,
  buildSearchBox,
  buildSearch,
  buildProductListing,
  buildCart,
  buildRelevanceSortCriterion,
  buildFieldsSortCriterion,
  buildRedirectionTrigger,
  SortDirection,
} from '@coveo/headless/commerce'
import { setGlobalDevModeChecks } from 'reselect'
import { getCoveoCommerceToken } from '../../api'
import { getFragment, isCoveoAnalyticsEnabled } from '../../shared/coveoUtils'

// Reselect v5 (used internally by @coveo/headless) runs an input-stability check in dev
// mode. Several headless selectors use `|| {}` / `|| []` fallbacks that create new object
// references on every call, causing false-positive "input selector returned different
// result" errors. setGlobalDevModeChecks is a global flag — also set in contentEngine.js
// so each engine file is self-contained.
setGlobalDevModeChecks({ inputStabilityCheck: 'never' })


// ---------------------------------------------------------------------------
// Sort criteria
// These don't depend on the engine so they can be created eagerly.
// Keyed by the legacy string values used across the existing search UI.
// ---------------------------------------------------------------------------
export const sortCriteria = {
  best_match:       buildRelevanceSortCriterion(),
  price_ascending:  buildFieldsSortCriterion([{ name: 'ec_price', direction: SortDirection.Ascending }]),
  price_descending: buildFieldsSortCriterion([{ name: 'ec_price', direction: SortDirection.Descending }]),
}

// ---------------------------------------------------------------------------
// Lazy singleton — engine and all controllers created once on first access
// ---------------------------------------------------------------------------
let _instance = null

/**
 * Lazily creates (or returns the existing) commerce engine + controllers.
 * Called once from SearchProvider — NOT at module scope.
 *
 * @param {object} coveoData  - Token/orgId from the server-side API call
 * @param {string} locale     - e.g. 'en_US'
 * @param {string} currencyCode - e.g. 'USD'
 * @param {string} salesOrg - e.g. 2400
 */

export function initCommerceEngine(coveoData, locale, currencyCode, salesOrg, userType) {
  if (_instance) return _instance

  if (typeof window === 'undefined') {
    console.warn('Coveo commerce engine requires a browser environment — skipping initialization')
    return null
  }
  const { coveoCommerceToken: token, organizationId, trackingId, entitlementQuery } = coveoData || {};
  if (!token || !organizationId || !trackingId) {
    console.warn('Coveo commerce engine requires token, organizationId, and trackingId — skipping initialization')
    return null
  }
  if (entitlementQuery === null) {
    console.warn('Coveo commerce engine requires a valid entitlementQuery — skipping initialization')
    return null
  }

  const [language, country] = (locale || 'en_US').split('_')
  const currency = currencyCode || 'USD'
  const resolvedSalesOrg = salesOrg || '2400'

  const engine = buildCommerceEngine({
    configuration: {
      organizationId: organizationId,
      accessToken: token,
      renewAccessToken: getCoveoCommerceToken,
      analytics: {
        trackingId: trackingId,
        enabled: isCoveoAnalyticsEnabled(),
      },
      context: {
        language,
        country,
        currency,
        view: {
          url: window.location.pathname,
        },
        custom: {
          ini_product_avail_sales_org: resolvedSalesOrg,
          ini_in_stock_status_sales_org: resolvedSalesOrg,
          ini_material_pricing_group_code_sales_org: resolvedSalesOrg,
          ini_show_on_web_sales_org: resolvedSalesOrg,
          ins_insight_price_incl_tax_sales_org: resolvedSalesOrg,
          ins_insight_price_sales_org: resolvedSalesOrg,
          ins_list_price_incl_tax_sales_org: resolvedSalesOrg,
          ins_list_price_sales_org: resolvedSalesOrg,
          ins_min_advertised_price_sales_org: resolvedSalesOrg,
          salesorg: resolvedSalesOrg,
          ...(userType ? { user_type: userType } : {}),
          inh_show_on_web_sales_org: resolvedSalesOrg,
          inh_material_pricing_group_code_sales_org: resolvedSalesOrg,
          inh_epeat_code_sales_org: resolvedSalesOrg,
          inh_item_cat_group_code_sales_org: resolvedSalesOrg,
          inh_sales_org_dist_chain_status: resolvedSalesOrg,
          inh_sales_org_material_active_status_code: resolvedSalesOrg,
          query_filter: entitlementQuery || '',
        },
      },
    },
  })

  // ─── Search box ─────────────────────────────────────────────────────────
  const searchBox = buildSearchBox(engine, { options: { numberOfSuggestions: 8 } })

  // ─── Product search controllers ─────────────────────────────────────────
  const search        = buildSearch(engine, { enableResults: true })
  const pagination    = search.pagination({ options: { pageSize: 50 } })
  const sort          = search.sort()
  const facetGenerator    = search.facetGenerator()
  const breadcrumbManager = search.breadcrumbManager()
  const didYouMean        = search.didYouMean()
  const summary           = search.summary()
  const redirectTrigger   = buildRedirectionTrigger(engine)
  const urlManager        = search.urlManager({
    initialState: { fragment: getFragment() },
    excludeDefaultParameters: true,
  })

  // ─── Product listing controllers ────────────────────────────────────────
  const productListing = buildProductListing(engine, { enableResults: true })
  const listingPagination         = productListing.pagination({ options: { pageSize: 50 } })
  const listingSort               = productListing.sort()
  const listingFacetGenerator     = productListing.facetGenerator()
  const listingBreadcrumbManager  = productListing.breadcrumbManager()
  const listingSummary            = productListing.summary()
  const listingUrlManager         = productListing.urlManager({
    initialState: { fragment: getFragment() },
    excludeDefaultParameters: true,
  })

  // ─── Cart ───────────────────────────────────────────────────────────────
  const cart = buildCart(engine)

  _instance = {
    engine,
    searchBox,
    didYouMean,
    cart,
    controllers: {
      search: {
        core:           search,
        pagination,
        sort,
        facetGenerator,
        breadcrumb:     breadcrumbManager,
        summary,
        redirectTrigger,
        urlManager,
      },
      listing: {
        core:           productListing,
        pagination:     listingPagination,
        sort:           listingSort,
        facetGenerator: listingFacetGenerator,
        breadcrumb:     listingBreadcrumbManager,
        summary:        listingSummary,
        urlManager:     listingUrlManager,
      },
    },
  }

  return _instance
}

/**
 * Updates the search box query text without triggering a search.
 * Useful for imperative access outside React (e.g. analytics callbacks, header sync).
 */
export function setQuery(q) {
  if (!_instance) return
  _instance.searchBox.updateText(q)
}

/**
 * Returns the current commerce engine instance, or null if not yet initialized.
 */
export function getInstance() {
  return _instance
}
